import { z } from "zod";
import { isValidUrl } from "@/lib/utils/url-utils";
import { commonSchemas } from "./common";
import type { ValidationT } from "./i18n";

/**
 * Vendor SHOP-CREATE wizard schemas (factory form, R6 schema-factory pattern).
 *
 * Each factory takes a `validation`-namespace translator so error messages
 * resolve through next-intl. Module-scope schemas can't call useTranslations,
 * so the consuming component/page builds these via
 * useMemo(() => makeVendorCreateSchema(t), [t]) with
 * const t = useTranslations("validation").
 */

// Basic info schema WITHOUT .refine() - refine is added in makeVendorCreateSchema
export const makeVendorBasicInfoSchema = (t: ValidationT) =>
  z.object({
    phone: z
      .string({
        error: t("phoneRequired"),
      })
      .min(1, t("phoneRequired"))
      .regex(/^(?:\+98|0)?(?:9\d{9}|[1-8]\d{9})$/, t("phoneInvalid")),
    shopName: z
      .string()
      .min(2, t("shopNameRequired"))
      .max(100, t("shopNameMax100Chars")),
    activityCategory: z
      .string({ error: t("activityCategoryRequired") })
      .min(1, t("activityCategoryRequired")),
    product: z.boolean().optional().default(false),
    service: z.boolean().optional().default(false),
    nationalCode: z
      .string({
        error: t("nationalCodeRequired"),
      })
      .length(10, t("nationalCode10Digits"))
      .regex(/^\d+$/, t("nationalCodeDigitsOnly"))
      .optional()
      .or(z.literal("")),
    email: z
      .string({
        error: t("emailRequired"),
      })
      .email(t("validEmailPlease"))
      .optional()
      .or(z.literal("")),
  });

export const makeVendorShopDetailsSchema = (t: ValidationT) =>
  z.object({
    logo: z
      .any()
      .refine((val) => val instanceof File || typeof val === "string", {
        message: t("logoRequired"),
      }),
    address: commonSchemas.optionalText(200),
    shortDescription: commonSchemas.optionalText(500),
    supportPhone: z
      .string()
      .regex(/^(?:\+98|0)?(?:9\d{9}|[1-8]\d{9})$/, t("validPhonePlease"))
      .optional()
      .or(z.literal("")),
    socialLinks: z
      .array(
        z.object({
          url: z.string().refine(
            (val) => {
              // Allow empty strings (for optional social links)
              if (!val || val.trim() === "") return true;
              // If URL is provided, validate it with TLD checking
              return isValidUrl(val);
            },
            {
              message: t("validUrlPlease"),
            },
          ),
        }),
      )
      .optional()
      .default([]),
    shippingMethod: z.array(z.uuid()).optional().or(z.literal("")),
    lat: z.number().optional(),
    lon: z.number().optional(),
  });

// Combined schema for vendor creation form (all steps)
// Note: .refine() must be called AFTER .safeExtend() because .refine() transforms
// ZodObject into ZodEffects, and .safeExtend() only works with ZodObject
export const makeVendorCreateSchema = (t: ValidationT) =>
  makeVendorBasicInfoSchema(t)
    .safeExtend(makeVendorShopDetailsSchema(t).shape)
    .refine((data) => data.product || data.service, {
      message: t("atLeastOneActivityType"),
      path: ["product"],
    });

// Type inference for vendor creation form
export type VendorCreateFormData = z.infer<
  ReturnType<typeof makeVendorCreateSchema>
>;

// Seller verification — identity info step (R6 schema-factory pattern)
export const makeSellerIdentityInfoSchema = (t: ValidationT) =>
  z.object({
    firstName: z
      .string({ error: t("firstNameRequired") })
      .min(1, t("firstNameRequired"))
      .max(50, t("firstNameMax50")),
    lastName: z
      .string({ error: t("lastNameRequired") })
      .min(1, t("lastNameRequired"))
      .max(50, t("lastNameMax50")),
    fatherName: z
      .string({ error: t("fatherNameRequired") })
      .min(1, t("fatherNameRequired"))
      .max(50, t("fatherNameMax50")),
    birthDay: z
      .string({ error: t("birthDayRequired") })
      .min(1, t("birthDayRequired")),
    birthMonth: z
      .string({ error: t("birthMonthRequired") })
      .min(1, t("birthMonthRequired")),
    birthYear: z
      .string({ error: t("birthYearRequired") })
      .min(1, t("birthYearRequired")),
    nationalCode: z
      .string({ error: t("nationalCodeRequired") })
      .length(10, t("nationalCode10Digits"))
      .regex(/^\d+$/, t("nationalCodeDigitsOnly")),
    phoneNumber: z
      .string({ error: t("phoneRequired") })
      .min(1, t("phoneRequired"))
      .regex(/^(?:\+98|0)?(?:9\d{9}|[1-8]\d{9})$/, t("phoneInvalid")),
  });

// Type inference for seller identity info form
export type SellerIdentityInfoFormData = z.infer<
  ReturnType<typeof makeSellerIdentityInfoSchema>
>;
